/* Progressive enhancement. Shopify remains the source of catalog, price and cart truth. */
(() => {
  'use strict';
  const configNode = document.getElementById('CisaroConfig');
  if (!configNode) return;
  const config = JSON.parse(configNode.textContent);
  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => [...root.querySelectorAll(selector)];
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const behavior = () => reducedMotion.matches ? 'instant' : 'smooth';
  const announce = (message) => { const region = $('[data-global-status]'); if (region) region.textContent = message; };
  let cartBusy = false;
  let variantRequest;
  let variantSequence = 0;
  let stickyObserver;
  const dialogOpeners = new WeakMap();

  function showDialog(dialog, opener) {
    if (!dialog || typeof dialog.showModal !== 'function') return false;
    $$('dialog[open]').forEach((other) => { if (other !== dialog) other.close(); });
    if (!dialog.open) {
      dialogOpeners.set(dialog, opener || document.activeElement);
      dialog.showModal();
    }
    document.body.classList.add('dialog-open');
    if (dialog.id === 'SearchDialog') $('[data-search-input]', dialog)?.focus();
    return true;
  }
  function closeDialog(dialog) { if (dialog?.open) dialog.close(); }
  function wireDialogs(root = document) {
    $$('[data-dialog]', root).forEach((dialog) => {
      if (dialog.dataset.bound) return;
      dialog.dataset.bound = 'true';
      dialog.addEventListener('close', () => {
        if (!$('dialog[open]')) document.body.classList.remove('dialog-open');
        const opener = dialogOpeners.get(dialog);
        if (opener?.isConnected) opener.focus({ preventScroll: true });
      });
      dialog.addEventListener('click', (event) => {
        if (event.target !== dialog) return;
        const rect = dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) closeDialog(dialog);
      });
    });
  }
  async function readJSON(response) {
    let data;
    try { data = await response.json(); } catch { throw new Error(config.strings.cartError); }
    if (!response.ok) {
      const message = typeof data.description === 'string' ? data.description : typeof data.message === 'string' ? data.message : config.strings.cartError;
      throw new Error(message);
    }
    return data;
  }
  const parseHTML = (text) => new DOMParser().parseFromString(text, 'text/html');
  function cartSectionIds() {
    const ids = ['cart-drawer'];
    const page = $('[data-cart-page]');
    if (page) ids.push(page.dataset.sectionId);
    return ids;
  }
  function setCartBusy(busy) {
    cartBusy = busy;
    $$('[data-cart-content]').forEach((content) => {
      content.setAttribute('aria-busy', String(busy));
      $$('button, input, textarea', content).forEach((control) => { control.disabled = busy; });
    });
  }
  function renderCart(sections) {
    const drawerHTML = sections?.['cart-drawer'];
    if (!drawerHTML) return false;
    const source = parseHTML(drawerHTML);
    const freshContent = $('[data-cart-render]', source);
    const targetContent = $('#CartDrawer [data-cart-render]');
    if (!freshContent || !targetContent) return false;
    targetContent.replaceChildren(...freshContent.childNodes);
    const count = $('[data-cart-content]', targetContent)?.dataset.itemCount || '0';
    $$('[data-cart-count], [data-drawer-count]').forEach((node) => { node.textContent = count; });
    const cartPage = $('[data-cart-page]');
    const draftNote = $('#CartNote')?.value;
    if (cartPage) {
      const pageHTML = sections[cartPage.dataset.sectionId];
      const fresh = pageHTML && $('[data-cart-render]', parseHTML(pageHTML));
      if (!fresh) return false;
      $('[data-cart-render]', cartPage).replaceChildren(...fresh.childNodes);
      if (draftNote !== undefined && $('#CartNote')) $('#CartNote').value = draftNote;
    }
    return true;
  }
  function cartError(message, origin) {
    const error = $('[data-cart-error]', origin?.closest('[data-cart-content]') || $('#CartDrawer'));
    if (error) { error.textContent = message; error.hidden = false; }
    announce(message);
  }
  async function addToCart(form) {
    if (form.dataset.submitting === 'true' || cartBusy) return;
    const buttons = $$('[data-add-button]').filter((button) => button.form === form);
    if (!buttons.length) buttons.push(...$$('[type="submit"]', form));
    const originalContents = buttons.map((button) => [...button.childNodes].map((node) => node.cloneNode(true)));
    const previousDisabled = buttons.map((button) => button.disabled);
    const error = $('[data-form-error]', form);
    if (error) { error.hidden = true; error.textContent = ''; }
    const body = new FormData(form);
    body.set('sections', cartSectionIds().join(','));
    body.set('sections_url', window.location.pathname + window.location.search);
    form.dataset.submitting = 'true';
    buttons.forEach((button) => { button.disabled = true; button.textContent = config.strings.adding; });
    setCartBusy(true);
    try {
      const response = await fetch(config.cartAddUrl, { method: 'POST', headers: { Accept: 'application/json' }, body });
      let data;
      try { data = await readJSON(response); } catch (e) {
        // Shopify's stock errors are actionable; a dropped response is not a reason to resubmit an order line.
        if (response.ok) throw new Error(config.strings.addError);
        throw e;
      }
      if (!renderCart(data.sections)) { window.location.assign(config.cartUrl); return; }
      showDialog($('#CartDrawer'), buttons[0]);
      announce(config.strings.added);
      document.dispatchEvent(new CustomEvent('cisaro:cart-added', { detail: { variantId: body.get('id'), quantity: Number(body.get('quantity') || 1) } }));
    } catch (e) {
      const message = e instanceof TypeError ? config.strings.addError : e.message;
      if (error) { error.textContent = message; error.hidden = false; }
      announce(message);
    } finally {
      setCartBusy(false);
      form.dataset.submitting = 'false';
      buttons.forEach((button, i) => { if (button.isConnected) { button.disabled = previousDisabled[i]; button.replaceChildren(...originalContents[i]); } });
    }
  }
  async function changeCart(line, quantity, focusId) {
    if (cartBusy || !line) return;
    const quantityInput = $('[data-cart-quantity]', line);
    if (!Number.isInteger(quantity) || quantity < 0 || (quantity > 0 && quantity < Number(quantityInput.dataset.min || 1))) {
      quantityInput.reportValidity();
      return;
    }
    const body = { id: line.dataset.lineKey, quantity, sections: cartSectionIds(), sections_url: window.location.pathname + window.location.search };
    setCartBusy(true);
    try {
      const data = await readJSON(await fetch(config.cartChangeUrl, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) }));
      if (!renderCart(data.sections)) { window.location.assign(config.cartUrl); return; }
      announce(`${data.item_count} ${data.item_count === 1 ? 'item' : 'items'} in your bag.`);
    } catch (e) { quantityInput.value = quantityInput.defaultValue; cartError(e.message || config.strings.cartError, line); }
    finally {
      setCartBusy(false);
      const focusTarget = focusId && document.getElementById(focusId);
      if (focusTarget) focusTarget.focus({ preventScroll: true });
      else if ($('#CartDrawer')?.open) $('#CartDrawer [data-close-dialog]')?.focus({ preventScroll: true });
    }
  }
  async function selectVariant(link) {
    const root = link.closest('[data-product-root]');
    const target = new URL(link.href, window.location.origin);
    // Combined listings navigate normally, preserving the correct complete product and app context.
    if (target.pathname !== window.location.pathname) { window.location.assign(target.href); return; }
    variantRequest?.abort();
    variantRequest = new AbortController();
    const sequence = ++variantSequence;
    root.setAttribute('aria-busy', 'true');
    $$('[data-add-button]', root).forEach((button) => { button.disabled = true; });
    const requestURL = new URL(target);
    requestURL.searchParams.set('section_id', root.dataset.sectionId);
    try {
      const response = await fetch(requestURL, { signal: variantRequest.signal, headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error('Variant request failed');
      const fresh = $('[data-product-root]', parseHTML(await response.text()));
      if (!fresh) throw new Error('Product section is unavailable');
      if (sequence !== variantSequence) return;
      root.replaceWith(fresh);
      history.replaceState({}, '', target.pathname + target.search);
      initializeProduct(fresh);
      $$('[data-option-id]', fresh).find((option) => option.dataset.optionId === link.dataset.optionId)?.focus({ preventScroll: true });
      announce(config.strings.variantUpdated);
      if (window.Shopify?.PaymentButton?.init) window.Shopify.PaymentButton.init();
    } catch (e) {
      if (e.name !== 'AbortError') window.location.assign(target.href);
    }
  }
  function initializeProduct(root = document) {
    const gallery = $('[data-gallery-track]', root);
    if (gallery && !gallery.dataset.bound) {
      gallery.dataset.bound = 'true';
      let frame;
      gallery.addEventListener('scroll', () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          const items = $$('[data-media-id]', gallery);
          const index = Math.round(gallery.scrollLeft / (gallery.clientWidth || 1));
          const mediaId = items[index]?.dataset.mediaId;
          $$('[data-media-target]', gallery.closest('[data-gallery]')).forEach((thumbnail) => {
            const active = thumbnail.dataset.mediaTarget === mediaId;
            thumbnail.classList.toggle('is-active', active);
            if (active) thumbnail.setAttribute('aria-current', 'true'); else thumbnail.removeAttribute('aria-current');
          });
          $$('video', gallery).forEach((video) => { if (video.closest('[data-media-id]')?.dataset.mediaId !== mediaId) video.pause(); });
        });
      }, { passive: true });
    }
    stickyObserver?.disconnect();
    const purchase = $('.product-form', root);
    const sticky = $('[data-sticky-purchase]', root);
    if (purchase && sticky && 'IntersectionObserver' in window) {
      stickyObserver = new IntersectionObserver(([entry]) => {
        sticky.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.bottom < 0);
      }, { threshold: 0 });
      stickyObserver.observe(purchase);
    }
    const plan = $('[data-selling-plan]', root);
    if (plan) applySellingPlan(plan);
    if ($('model-viewer', root) && window.Shopify?.loadFeatures) {
      window.Shopify.loadFeatures([{ name: 'model-viewer-ui', version: '1.0', onLoad: () => {
        if (window.Shopify.ModelViewerUI) $$('model-viewer', root).forEach((model) => { if (!model.dataset.enhanced) { new window.Shopify.ModelViewerUI(model); model.dataset.enhanced = 'true'; } });
      } }]);
    }
  }
  function applySellingPlan(select) {
    const root = select.closest('[data-product-root]');
    const price = select.selectedOptions[0]?.dataset.price;
    if (!price) return;
    $$('.product-price .price, .sticky-purchase .price', root).forEach((node) => { node.textContent = price; });
  }
  async function recommendations(root = document) {
    for (const host of $$('[data-recommendations]', root)) {
      if (host.dataset.loaded) continue;
      host.dataset.loaded = 'true';
      try {
        const response = await fetch(host.dataset.url);
        if (!response.ok) continue;
        const content = $('[data-recommendations]', parseHTML(await response.text()));
        if (content) host.replaceChildren(...content.childNodes);
      } catch { /* Optional recommendations do not interrupt shopping. */ }
    }
  }
  function wireSearch() {
    const input = $('[data-search-input]');
    const host = $('[data-predictive-results]');
    if (!input || !host || input.dataset.bound) return;
    input.dataset.bound = 'true';
    let timer, controller, sequence = 0;
    input.addEventListener('input', () => {
      clearTimeout(timer);
      controller?.abort();
      const currentSequence = ++sequence;
      const query = input.value.trim();
      if (query.length < 2) { host.replaceChildren(); host.removeAttribute('aria-busy'); return; }
      timer = setTimeout(async () => {
        controller = new AbortController();
        host.setAttribute('aria-busy', 'true');
        try {
          const url = new URL(config.predictiveUrl, window.location.origin);
          url.searchParams.set('q', query);
          url.searchParams.set('resources[type]', 'product');
          url.searchParams.set('resources[limit]', '4');
          url.searchParams.set('section_id', 'predictive-search');
          const response = await fetch(url, { signal: controller.signal });
          if (!response.ok) throw new Error('Suggestions unavailable');
          const content = $('[data-predictive-content]', parseHTML(await response.text()));
          if (currentSequence === sequence) host.replaceChildren(...(content ? content.childNodes : []));
        } catch (e) { if (e.name !== 'AbortError' && currentSequence === sequence) host.replaceChildren(); }
        finally { if (currentSequence === sequence) host.removeAttribute('aria-busy'); }
      }, 230);
    });
    input.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowDown') { const first = $('a', host); if (first) { event.preventDefault(); first.focus(); } }
    });
  }
  function wireAddresses(root = document) {
    $$('[data-address-country]', root).forEach((country) => {
      if (country.dataset.bound) return;
      country.dataset.bound = 'true';
      const fieldset = country.closest('[data-address-fields]');
      const province = $('[data-address-province]', fieldset);
      const initial = country.dataset.default;
      if (initial) {
        const option = [...country.options].find((item) => item.value === initial || item.text === initial);
        if (option) country.value = option.value;
      }
      const update = (useDefault = false) => {
        let options = [];
        try { options = JSON.parse(country.selectedOptions[0]?.dataset.provinces || '[]'); } catch { /* Keep an empty province list. */ }
        province.replaceChildren(...options.map(([value, label]) => new Option(label, value)));
        $('[data-province-wrapper]', fieldset).hidden = options.length === 0;
        province.disabled = options.length === 0;
        if (useDefault && province.dataset.default) province.value = province.dataset.default;
      };
      update(true);
      country.addEventListener('change', () => update(false));
    });
  }
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (form.matches('[data-delete-address]') && !window.confirm('Delete this address from your account?')) { event.preventDefault(); return; }
    if (form.matches('[data-product-form]') && window.fetch) {
      if (!form.reportValidity()) return;
      event.preventDefault();
      addToCart(form);
    }
    if (event.submitter?.matches('[data-checkout]')) {
      if (cartBusy) event.preventDefault();
      else document.dispatchEvent(new CustomEvent('cisaro:checkout-started'));
    }
  });
  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const opener = event.target.closest('[data-open-dialog]');
    if (opener && !event.metaKey && !event.ctrlKey && !event.shiftKey && showDialog(document.getElementById(opener.dataset.openDialog), opener)) event.preventDefault();
    const menuLink = event.target.closest('[data-menu] a');
    if (menuLink) menuLink.closest('[data-menu]').open = false;
    const closer = event.target.closest('[data-close-dialog]');
    if (closer) closeDialog(closer.closest('dialog'));
    const recover = event.target.closest('a[href="#RecoverPassword"]');
    if (recover) { const details = $('#RecoverPassword'); if (details) details.open = true; }
    const variantLink = event.target.closest('[data-variant-link]');
    if (variantLink && !event.metaKey && !event.ctrlKey && !event.shiftKey && event.button === 0) { event.preventDefault(); if (!cartBusy) selectVariant(variantLink); }
    const quantityButton = event.target.closest('[data-quantity-step]');
    if (quantityButton) {
      const field = quantityButton.closest('.quantity-field');
      const input = $('input', field);
      const line = input.closest('[data-cart-line]');
      if (line && cartBusy) return;
      const step = Number(input.step) || 1;
      const min = Number(input.min) || 0;
      const max = input.max === '' ? Infinity : Number(input.max);
      let quantity = Math.max(min, Math.min(max, (Number(input.value) || min) + Number(quantityButton.dataset.quantityStep) * step));
      const ruleMin = Number(input.dataset.min || min);
      if (line && quantity > 0 && quantity < ruleMin) quantity = quantityButton.dataset.quantityStep === '-1' ? 0 : ruleMin;
      if (line) changeCart(line, quantity, input.id);
      else { input.value = String(quantity); input.dispatchEvent(new Event('change', { bubbles: true })); }
    }
    const remove = event.target.closest('[data-remove-item]');
    if (remove) { event.preventDefault(); changeCart(remove.closest('[data-cart-line]'), 0); }
    const thumbnail = event.target.closest('[data-media-target]');
    if (thumbnail) {
      event.preventDefault();
      const track = $('[data-gallery-track]', thumbnail.closest('[data-gallery]'));
      const items = $$('[data-media-id]', track);
      const index = items.findIndex((item) => item.dataset.mediaId === thumbnail.dataset.mediaTarget);
      track.scrollTo({ left: index * track.clientWidth, behavior: behavior() });
    }
    const direction = event.target.closest('[data-gallery-direction]');
    if (direction) {
      const track = $('[data-gallery-track]', direction.closest('[data-gallery]'));
      track.scrollBy({ left: Number(direction.dataset.galleryDirection) * track.clientWidth, behavior: behavior() });
    }
    const zoom = event.target.closest('[data-zoom-image]');
    if (zoom && !event.metaKey && !event.ctrlKey) {
      let dialog = $('#ImageZoom');
      if (!dialog && typeof HTMLDialogElement !== 'undefined') {
        dialog = document.createElement('dialog');
        dialog.id = 'ImageZoom'; dialog.className = 'zoom-dialog'; dialog.dataset.dialog = ''; dialog.setAttribute('aria-label', 'Product image');
        const button = document.createElement('button'); button.type = 'button'; button.className = 'icon-button'; button.dataset.closeDialog = ''; button.setAttribute('aria-label', 'Close image'); button.textContent = '×';
        dialog.append(button, document.createElement('img')); document.body.append(dialog); wireDialogs();
      }
      if (dialog && typeof dialog.showModal === 'function') {
        $('img', dialog).src = zoom.href; $('img', dialog).alt = $('img', zoom)?.alt || '';
        event.preventDefault(); showDialog(dialog, zoom);
      }
    }
    $$('[data-menu][open], .nav-dropdown[open], .facet[open]').forEach((details) => { if (!details.contains(event.target)) details.open = false; });
  });
  document.addEventListener('change', (event) => {
    if (event.target.matches('[data-cart-quantity]')) {
      const input = event.target;
      if (input.reportValidity()) changeCart(input.closest('[data-cart-line]'), Number(input.value), input.id);
    }
    if (event.target.matches('[data-selling-plan]')) applySellingPlan(event.target);
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') $$('[data-menu][open], .nav-dropdown[open], .facet[open]').forEach((details) => { details.open = false; $('summary', details)?.focus(); });
  });
  document.addEventListener('shopify:section:load', (event) => {
    wireDialogs(event.target); wireSearch(); initializeProduct(); recommendations(event.target); wireAddresses(event.target);
  });
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      const url = new URL(window.location.href); url.searchParams.set('sections', cartSectionIds().join(','));
      fetch(url).then(readJSON).then(renderCart).catch(() => {});
    }
  });
  wireDialogs(); wireSearch(); initializeProduct(); recommendations(); wireAddresses();
  if (window.location.hash === '#RecoverPassword') { const recover = $('#RecoverPassword'); if (recover) recover.open = true; }
})();
